import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { MOBILE_BREAKPOINT } from './utils';
import { HQ_CONTRACT_API } from './contractApi';

// api: 이 화면이 부를 주소 묶음(contractApi.js). 기본은 본사 묶음 — 지금까지와 같은 주소(2026-10-15, 4-2).
export function useContractManagement(api = HQ_CONTRACT_API) {
  const { showToast } = useToast();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [contracts, setContracts] = useState([]);
  // 목록 상한 정보 { total, truncated, cap } — 상한(3000)을 넘으면 화면에 알림 한 줄(2026-10-13)
  const [listMeta, setListMeta] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [users, setUsers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [contractTicketTypes, setContractTicketTypes] = useState([]);
  const [externalCategories, setExternalCategories] = useState([]);
  const [allProductCategories, setAllProductCategories] = useState([]);
  const [promotionalItemsList, setPromotionalItemsList] = useState([]);
  const [formDesignCourier, setFormDesignCourier] = useState('');
  // 계약 등록 창에 보일 칸 설정 (⚙️ 설정에서 정한다). 못 읽으면 null → 모달이 기존 화면 그대로 뜬다.
  const [fieldConfig, setFieldConfig] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [isReorder, setIsReorder] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logModalRow, setLogModalRow] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchContracts();
    fetchUsers();
    fetchSalesPersons();
    fetchContractTicketTypes();
    fetchExternalCategories();
    fetchPromotionalItems();
    fetchFormDesignCourier();
    refreshFieldConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 설정 모달에서 저장하면 다시 읽어 계약 모달에 곧바로 반영한다
  const refreshFieldConfig = useCallback(async () => {
    const res = await apiClient.get(api.fieldConfig);
    if (res.ok && res.data?.config) setFieldConfig(res.data.config);
  }, [api.fieldConfig]);

  async function fetchContracts() {
    setLoadingList(true);
    const res = await apiClient.get(api.contracts);
    setLoadingList(false);
    if (!res.ok) {
      showToast('계약 목록 조회 실패', 'error');
      return;
    }
    setContracts(Array.isArray(res.data?.data) ? res.data.data : []);
    setListMeta(res.data?.meta || null);
  }

  async function fetchContractTicketTypes() {
    const res = await apiClient.get(api.ticketTypes);
    if (!res.ok) return;
    setContractTicketTypes(Array.isArray(res.data?.data) ? res.data.data : []);
  }

  async function fetchPromotionalItems() {
    if (!api.promoItemsActive) return; // 회사별·통합 화면 = 홍보물 보기만(4-5) — 주문 넣기 칸이 없어 품목 목록이 필요 없다
    const res = await apiClient.get(api.promoItemsActive);
    if (!res.ok) return;
    setPromotionalItemsList(Array.isArray(res.data?.data) ? res.data.data : []);
  }

  async function fetchFormDesignCourier() {
    const res = await apiClient.get(api.promoSuppliers);
    if (!res.ok) return;
    const suppliers = Array.isArray(res.data?.data) ? res.data.data : [];
    const formDesign = suppliers.find((s) => s.name === '폼디자인');
    if (formDesign?.default_courier) setFormDesignCourier(formDesign.default_courier);
  }

  async function fetchExternalCategories() {
    const res = await apiClient.get(api.productCategories);
    if (!res.ok) return;
    const all = Array.isArray(res.data?.data) ? res.data.data : [];
    // 분류 이름 → 기본상품 치환표. 서버(listContractTicketTypes)와 같은 기준으로 맞춘다 —
    //   기본상품이 지정된 분류만. 내부/외부 구분은 없앴다(2026-09-20).
    setExternalCategories(all.filter((c) => c.default_product_id));
    setAllProductCategories(all);
  }

  async function fetchUsers() {
    const res = await apiClient.get(api.callUsers);
    if (!res.ok) return;
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
    setUsers(rows.map((u) => ({ id: u.id, name: u.name })));
  }

  async function fetchSalesPersons() {
    const res = await apiClient.get(api.salesPersons);
    if (!res.ok) return;
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
    setSalesPersons(rows.map((sp) => ({ id: sp.id, name: sp.name })));
  }

  const handleSearch = useCallback(async () => {
    const q = searchQ.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    const res = await apiClient.get(`${api.contracts}/search?q=${encodeURIComponent(q)}`);
    setSearching(false);
    if (!res.ok) {
      showToast(res.data?.message || '검색 실패', 'error');
      return;
    }
    setSearchResults(Array.isArray(res.data?.data) ? res.data.data : []);
  }, [searchQ, showToast, api.contracts]);

  function onKeyDown(e) {
    if (e.key === 'Enter') handleSearch();
  }

  // 수정 창은 **그 줄의 계약**으로 연다(contract_id = 진짜 계약 번호, 목록·검색 줄 모두 있음).
  //   2026-10-12: 전엔 콜 계약을 by-call(그 콜의 최신 계약)로 열어, 콜 하나에 재주문이 붙으면 예전 계약 줄을 눌러도
  //   최신 재주문이 열리고 [변경 저장]도 그 재주문에 들어갔다(파인그룹 김포한강오토캠핑장 10/8·10/9).
  //   계약 번호가 없는 줄(계약 행 없는 옛 콜)만 예전처럼 by-call. 재주문 버튼(openReorder)은 최신 계약으로 채우는 게 맞아 그대로.
  async function loadLatestContractRow(row) {
    const isDirectContract = row.is_direct_contract === true || !row.tm_call_id;
    const contractId = row.contract_id ?? (isDirectContract ? (row.direct_contract_id ?? row.id) : null);
    const endpoint = contractId
      ? `${api.contracts}/by-contract/${encodeURIComponent(contractId)}`
      : `${api.contracts}/by-call/${encodeURIComponent(row.tm_call_id ?? row.id)}`;
    const res = await apiClient.get(endpoint);
    if (!res.ok || !res.data?.data) return null;
    return res.data.data;
  }

  async function openEdit(row) {
    let editRow = row;
    if (row.is_contracted) {
      const latestRow = await loadLatestContractRow(row);
      if (!latestRow) {
        showToast('최신 계약 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.', 'error');
        return;
      }
      editRow = latestRow;
    }
    setModalRow(editRow);
    setIsNew(false);
    setIsReorder(false);
    setModalOpen(true);
  }

  function openNew() {
    setModalRow(null);
    setIsNew(true);
    setIsReorder(false);
    setModalOpen(true);
  }

  async function openReorder(row) {
    const isDirectContract = row.is_direct_contract === true || !row.tm_call_id;
    let res;
    if (isDirectContract) {
      const contractId = row.direct_contract_id ?? row.id;
      res = await apiClient.get(`${api.contracts}/by-contract/${encodeURIComponent(contractId)}`);
    } else {
      const tmCallId = row.tm_call_id ?? row.id;
      res = await apiClient.get(`${api.contracts}/by-call/${encodeURIComponent(tmCallId)}`);
    }
    const latestRow = res.ok && res.data?.data ? res.data.data : row;
    setModalRow(latestRow);
    setIsNew(true);
    setIsReorder(true);
    setModalOpen(true);
  }

  function handleDeleteRequest(row) {
    setDeleteTargetRow(row);
    setDeleteConfirmOpen(true);
  }

  function handleHistory(row) {
    setLogModalRow(row);
    setLogModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deleteTargetRow) return;
    setDeleting(true);
    try {
      const res = await apiClient.del(`${api.contracts}/${deleteTargetRow.contract_id ?? deleteTargetRow.id}`);
      setDeleteConfirmOpen(false);
      setDeleteTargetRow(null);
      if (!res.ok) {
        showToast(res.data?.message || '계약 삭제에 실패했습니다.', 'error');
        return;
      }
      showToast('계약을 삭제했습니다. 미계약 입력 상태로 되돌렸습니다.', 'success');
      onSaved();
    } finally {
      setDeleting(false);
    }
  }

  async function onSaved() {
    fetchContracts();
    if (searchResults !== null && searchQ.trim()) {
      const res = await apiClient.get(`${api.contracts}/search?q=${encodeURIComponent(searchQ.trim())}`);
      if (res.ok) setSearchResults(Array.isArray(res.data?.data) ? res.data.data : []);
    }
  }

  function clearSearch() {
    setSearchResults(null);
    setSearchQ('');
  }

  return {
    isMobile, contracts, listMeta, loadingList,
    searchQ, setSearchQ, searchResults, searching, clearSearch,
    users, salesPersons, contractTicketTypes,
    externalCategories, allProductCategories, promotionalItemsList,
    formDesignCourier,
    fieldConfig, refreshFieldConfig,
    modalOpen, setModalOpen, modalRow, isNew, isReorder, setIsReorder,
    deleteConfirmOpen, setDeleteConfirmOpen,
    deleteTargetRow, setDeleteTargetRow,
    deleting, setDeleting,
    logModalOpen, setLogModalOpen,
    logModalRow, setLogModalRow,
    handleSearch, onKeyDown,
    openEdit, openNew, openReorder,
    handleDeleteRequest, handleHistory, handleConfirmDelete,
    onSaved,
  };
}
